/**
 * Product display title — FRIDGE-134.
 *
 * WHAT WAS WRONG
 * 1,189 of 5,524 active products shared a title with another product, in 518
 * groups, and the PDP rendered that raw feed title as both the H1 and the
 * keyword segment of the <title>. Two different Frigidaire models (VB black,
 * VS stainless) served a byte-identical H1; three different Whirlpool models
 * shared one title. Nothing on the page said which product it was.
 *
 * THE RULE
 * A product page must name the product. The model number is the one attribute
 * that is present on 100% of active rows and is unique per product, so the
 * display title is the feed title with the shopper-facing model appended when
 * the title does not already contain it.
 *
 * WHY UNCONDITIONALLY, NOT ONLY ON COLLISIONS
 * Appending only when a title currently collides would make each page's title
 * depend on the rest of the catalog: a feed import that adds an unrelated
 * product would silently rewrite a different page's H1. Deriving the title
 * from the row's own data keeps it stable and deterministic, and matches how
 * appliance retailers title model-level pages.
 *
 * WHAT IS DELIBERATELY NOT DONE HERE
 * No finish or colour is invented. Where a feed title asserts a finish that
 * may be wrong for the model (the three Whirlpool rows all claim "in Black"
 * while `color` is NULL on all three), appending the model makes the page
 * IDENTIFY the right product but does not make the feed's finish claim true.
 * Correcting that needs finish data, not a title rule.
 *
 * The shopper-facing model must come from resolveDisplayModel (lib/url-format),
 * which returns '' for an ASIN or the __PENDING_REVIEW__ sentinel — neither may
 * ever reach a title.
 */

import { resolveDisplayModel, nonApplianceTypeToSlug } from '@/lib/url-format';
import { parsePackQuantity } from '@/lib/filters/pack';
import { TITLE_TARGET_CHARS } from '@/lib/seo/page-title';

/** Comparison key: case- and separator-insensitive, so "KOS-36RFD07T" is
 *  recognised inside a title that writes it "KOS 36RFD07T" or "kos36rfd07t". */
function modelKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * An ASIN must never render as a model (Rule 6, "ASIN is HIDDEN sitewide").
 * 412 active rows carry one behind a numeric feed prefix — "3883_B075QBS8DV" —
 * and putting that in a title would leak an internal identifier onto 412 pages,
 * so this rejects the prefixed shape as well as the bare one.
 *
 * As of FRIDGE-110 (08/16/2026) the shared `resolveDisplayModel` rejects the
 * prefixed shape too, so the PDP "Model:" row, the verdict sentence and the
 * meta keywords no longer print it either. This check is kept as the title
 * path's own guard: the two rules are pinned equal in
 * tests/display-model-feed-prefixed-asin.test.ts, so they cannot drift apart.
 */
function isAsinLike(value: string): boolean {
  const bare = value.trim().replace(/^\d+_/, '');
  return /^B0[A-Z0-9]{8}$/i.test(bare);
}

/**
 * The model a TITLE may use, or '' when none is usable. This is the single
 * rule the H1, the <title> and the duplicate-title check all share, so a
 * report can never disagree with what a page actually renders.
 */
export function resolveTitleModel(
  rawModel: string | null | undefined,
  manufacturerModel: string | null | undefined = null
): string {
  const model = resolveDisplayModel(rawModel, manufacturerModel);
  return !model || isAsinLike(model) ? '' : model;
}

/** True when the title already names this model, in any separator style. */
export function titleContainsModel(title: string, displayModel: string): boolean {
  const key = modelKey(displayModel);
  if (!key) return false;
  return modelKey(title).includes(key);
}

/**
 * A model number renders exactly as stored. The title formatter upstream
 * (formatBreadcrumbText) capitalises words, and before FRIDGE-348 (08/17/2026)
 * it took "WRFC5036RZ" along for the ride: the live H1 read "… French Door
 * Refrigerator Wrfc5036rz" while the <title> said "WRFC5036RZ". When the title
 * already carries the model as a contiguous run in any casing, put the stored
 * casing back; separator-different spellings ("KOS 36RFD07T" for
 * "KOS-36RFD07T") are left alone — recognised, not rewritten.
 */
export function restoreModelCasing(title: string, displayModel: string): string {
  const model = (displayModel ?? '').trim();
  if (!title || !model) return title;
  const at = title.toLowerCase().indexOf(model.toLowerCase());
  if (at === -1) return title;
  return title.slice(0, at) + model + title.slice(at + model.length);
}

/**
 * The H1 / display title: the feed title, with the model appended when absent.
 * Returns the title unchanged when there is no displayable model — an ASIN or
 * the placeholder sentinel must never be printed.
 */
export function buildProductDisplayTitle(
  title: string | null | undefined,
  displayModel: string | null | undefined
): string {
  const base = (title ?? '').trim();
  const model = (displayModel ?? '').trim();
  if (!base) return '';
  if (!model || isAsinLike(model)) return base;
  if (titleContainsModel(base, model)) return restoreModelCasing(base, model);
  return `${base} ${model}`;
}

/**
 * The keyword segment of the owner-binding title formula
 * (`Target Keyword | Benefit | Brand`).
 *
 * The model must survive truncation, or the fix is undone by the trim that
 * produced the near-identical titles in the first place: the two Frigidaire
 * pages already differed only in a truncated trailing fragment. So the
 * DESCRIPTIVE part is trimmed to fit and the model is appended afterwards,
 * never trimmed off the end.
 *
 * With no model to add, this reproduces the previous trimming exactly, so
 * titles that already carry their model are unchanged byte for byte.
 *
 * @param overhead  length of ` | ${benefit} | Fridge.com` — the segments that
 *                  must never be trimmed.
 */
export function buildTitleKeywordSegment(
  title: string | null | undefined,
  displayModel: string | null | undefined,
  overhead: number,
  // FRIDGE-1138: the one shared title target, not a product-only 65.
  maxLength: number = TITLE_TARGET_CHARS
): string {
  let base = (title ?? '').trim();
  if (!base) return '';
  const model = (displayModel ?? '').trim();
  const usable = !!model && !isAsinLike(model);
  let suffix = !usable || titleContainsModel(base, model) ? '' : ` ${model}`;

  const fits = (b: string, sfx: string) => b.length + sfx.length + overhead <= maxLength || b.length <= 25;

  // FRIDGE-1123: a feed title that already NAMES its model usually names it at
  // the end ("AccuCold 24 Inch All-Refrigerator ARG15PV"), which is exactly
  // where the trim cuts — 32 AccuCold pages collided on "AccuCold 24 Inch 24…".
  // When the line runs long, lift the model out of the descriptive part and
  // re-append it after the trim, the same model-survives-trim rule as a model
  // the feed omitted. A title that fits is returned byte for byte.
  if (usable && suffix === '' && !fits(base, suffix)) {
    const lifted = base.replace(modelPattern(model), ' ').replace(/\s{2,}/g, ' ').replace(/[\s:;,&–—-]+$/, '').trim();
    if (lifted && lifted !== base) {
      base = lifted;
      suffix = ` ${model}`;
    }
  }

  // Room for the descriptive part once the brand, benefit and model are paid for.
  const budget = Math.max(25, maxLength - overhead) - suffix.length;

  if (fits(base, suffix)) {
    return `${base}${suffix}`;
  }

  // Word boundary whenever one exists past the first word: a lifted model
  // shortens the budget, and "Kostch French Doo…" is not a keyword.
  const cut = base.lastIndexOf(' ', budget);
  const trimmed = base
    .substring(0, cut > 5 ? cut : Math.max(1, budget))
    .replace(/[\s:;,&–—-]+$/, '')
    .trim();
  return `${trimmed}…${suffix}`;
}

/** The model as it may appear in a feed title: any run of spaces, hyphens or
 *  slashes between its characters (the titleContainsModel tolerance). */
function modelPattern(model: string): RegExp {
  const chars = model.replace(/[^a-z0-9]/gi, '').split('');
  const body = chars.map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[\\s/-]*');
  return new RegExp(`(^|[\\s(,:-])${body}(?=$|[\\s),.:;-])`, 'i');
}

/**
 * FRIDGE-538 — the H1 for a part or filter, derived from what the thing IS
 * rather than from the marketplace title it was imported with.
 *
 * WHAT THIS FIXES, measured live 08/25/2026 across the 434 live part and filter
 * pages:
 *
 *   titles over 80 characters ............... 358  (83%)
 *   titles over 110 characters .............. 325
 *   average title length .................... 138
 *   containing a compatibility list ......... 315  (73%)
 *      — versus 7 of 5,328 appliance titles (0.13%)
 *
 * A real one, rendered as an <h1> today:
 *
 *   "Makspro 2159061 Gasket Door Fresh Food Beige Fits Refrigerator 2171377
 *    2183851 2202875 2214963 MP-2159061"
 *
 * That is a compatibility list, which belongs in the "Fits these refrigerators"
 * block (FRIDGE-397), not in the heading. Several are also hard-capped at 200
 * characters and cut MID-WORD.
 *
 * WHY IT RETURNS null RATHER THAN A BEST EFFORT
 * ---------------------------------------------
 * 237 of the 434 rows store an ASIN in `model` — no real part number is
 * recorded for them. Two dishonest options were rejected:
 *
 *   • printing the ASIN — `isAsinLike` exists precisely to stop that;
 *   • dropping the model and rendering "<Brand> <What it is>" — clean, but it
 *     produces IDENTICAL headings across every Icepure water filter. This site
 *     has already been bitten by near-identical titles, and trading a stuffed
 *     heading for a duplicated one is not an improvement.
 *
 * So it builds a title only when brand AND a real part number are both present
 * — 197 of 434 — and returns null otherwise, leaving the caller on the existing
 * marketplace title. The remaining 237 need a part number sourced; that is a
 * data gap, not a rendering one, and it should not be papered over here.
 *
 * The type's human name is DERIVED from NON_APPLIANCE_TYPE_SLUG_MAP rather than
 * duplicated, so the heading and the URL can never disagree about what a thing
 * is: `gasket` → `refrigerator-gasket` → "Refrigerator Gasket".
 */
export function nonApplianceTypeDisplayName(type: string | null | undefined): string | null {
  const slug = nonApplianceTypeToSlug(type);
  if (!slug) return null;
  return slug
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/**
 * FRIDGE-547 — the ASIN-only rows, named by what they REPLACE.
 *
 * 237 of the 434 live part and filter rows hold an Amazon ASIN in `model` and
 * no manufacturer part number. Checked 08/25/2026, we hold no substitute
 * anywhere: `upc` is populated on 0 of them, `normalized_model` on 0, and
 * `specs_json` carries no `mpn` key at all (its keys are amazon_asin,
 * data_source, filter_category, lifespan_months, oem_filter_type, pack_size).
 *
 * THE TITLE ROUTE IS NOT AVAILABLE, AND THE SAMPLE PROVES IT. Every one of these
 * titles leads with the part it REPLACES, belonging to a different manufacturer:
 *
 *   "4u Pure"       -> EPTWFU01, WF3CB        (Frigidaire's)
 *   "Amazon Basics" -> MWF                    (GE's)
 *   "Amazon Basics" -> DA97-17376B            (Samsung's)
 *   "Amazon Basics" -> LT700P                 (LG's)
 *
 * Writing any of those into `model` would assign another manufacturer's identity
 * and make every competing aftermarket brand collide on it.
 *
 * BUT IT IS A PERFECTLY GOOD COMPATIBILITY STATEMENT, and we already store it
 * normalised in `specs_json.oem_filter_type`. Said as "for MWF" rather than as
 * this product's number, it is true, it is what a buyer actually matches on, and
 * it invents nothing.
 *
 * COVERAGE, measured: 217 of the 237 have both an OEM type and a brand, which
 * lifts the honest-heading path from 197 to 414 of 434. Uniqueness was checked
 * rather than assumed — brand + type + OEM alone collides on 41 rows (one brand
 * selling several pack sizes of the same filter), so `pack_size` is appended
 * when it is above 1. With it, all 217 headings are distinct.
 *
 * The remaining 20 have no OEM type either and keep returning null, which is the
 * fallback that already exists and works.
 */
export function buildNonApplianceDisplayTitle(input: {
  brand: string | null | undefined;
  type: string | null | undefined;
  model: string | null | undefined;
  /** `specs_json.oem_filter_type` — the OEM part this REPLACES, never its own. */
  oemFilterType?: string | null | undefined;
  /** `specs_json.pack_size` — appended only above 1, to keep headings distinct. */
  packSize?: string | number | null | undefined;
  /** Stored listing title supplies explicit pack wording when the field is absent. */
  listingTitle?: string | null | undefined;
  /**
   * FRIDGE-1123 — the page's own identifier (FFN/FPN), appended ONLY on the
   * "for <OEM part>" path. That path names what a listing replaces, and one
   * aftermarket brand sells many listings for one OEM part (alkaline, lead-
   * reduction, bundle): measured 09/29/2026, 16 title groups over 60 live filter
   * pages, every one on this path. The identifier is the one stored fact that
   * differs. The <title> passes it; the visible heading does not.
   */
  compatibilityIdentifier?: string | null | undefined;
}): string | null {
  const what = nonApplianceTypeDisplayName(input.type);
  if (!what) return null; // an appliance, or a type we do not name — caller falls back

  const brand = (input.brand ?? '').trim();
  const model = (input.model ?? '').trim();
  const pack = String(input.packSize ?? parsePackQuantity(input.listingTitle) ?? '').trim();
  const packSuffix = /^\d+$/.test(pack) && Number(pack) > 1 ? ` (${pack}-Pack)` : '';

  // No usable part number of its own: name it by what it replaces, if we know.
  if (brand && (!model || isAsinLike(model))) {
    const oem = (input.oemFilterType ?? '').trim();
    if (!oem) return null;
    // "for X" is a COMPATIBILITY claim, deliberately not "X" as an identity.
    const id = (input.compatibilityIdentifier ?? '').trim();
    return `${collapseBrandNounStutter(brand, what)} for ${oem.toUpperCase()}${packSuffix}${id ? ` (${id})` : ''}`;
  }

  if (!brand || !model || isAsinLike(model)) return null;

  // A model that is itself a compatibility list is not a part number. The same
  // guard the slug builder applies (FRIDGE-144) — a heading must not carry one
  // either, and the row needs human review before it can have a clean title.
  const modelShapedTokens = new Set(
    model.split(/[\s,/|]+/).filter((t) => /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9-]{3,}$/.test(t))
  ).size;
  if (modelShapedTokens > 1) return null;

  // Do not repeat the brand when the part number already carries it.
  if (new RegExp(`^${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(model)) {
    return `${what} ${model}${packSuffix}`;
  }

  const collapsed = collapseBrandNounStutter(brand, what);
  return collapsed === brand ? `${brand} ${model}${packSuffix}` : `${collapsed} ${model}${packSuffix}`;
}

/**
 * "Continental Refrigerator" + "Refrigerator Gasket" rendered "Continental
 * Refrigerator Refrigerator Gasket" — caught on a live page, not in theory.
 * Where the brand's trailing words are the type name's leading words, the
 * overlap is written once. Shared by both heading paths so the ASIN path cannot
 * reintroduce the stutter the other one fixed.
 */
function collapseBrandNounStutter(brand: string, what: string): string {
  const bw = brand.split(/\s+/);
  const ww = what.split(/\s+/);
  let overlap = 0;
  for (let n = Math.min(bw.length, ww.length); n > 0; n--) {
    const tail = bw.slice(bw.length - n).join(' ').toLowerCase();
    if (tail === ww.slice(0, n).join(' ').toLowerCase()) {
      overlap = n;
      break;
    }
  }
  const noun = ww.slice(overlap).join(' ');
  return noun ? `${brand} ${noun}` : brand;
}
